import { cn } from '@quadralize/utils'
import { type HTMLMotionProps, motion } from 'framer-motion'
import * as React from 'react'

interface StatCardProps extends Omit<HTMLMotionProps<'div'>, 'children'> {
  label: string
  value: string | number
  description?: string
  icon?: React.ReactNode
  trend?: {
    value: number
    label: string
    isPositive: boolean
  }
}

const StatCard = React.forwardRef<HTMLDivElement, StatCardProps>(
  ({ label, value, description, icon, trend, className, ...props }, ref) => {
    return (
      <motion.div
        ref={ref}
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.3 }}
        className={cn(
          'group relative overflow-hidden rounded-xl border border-border-default bg-bg-base p-4 transition-all hover:shadow-lg md:p-6 dark:bg-bg-subtle',
          className,
        )}
        {...props}
      >
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-medium text-text-tertiary md:text-sm">{label}</p>
            <h3 className="mt-1 text-base font-bold tracking-tight text-text-primary md:text-2xl">
              {value}
            </h3>
          </div>
          {icon && (
            <div className="hidden rounded-lg bg-bg-muted p-2 text-text-secondary transition-colors group-hover:bg-primary/10 group-hover:text-primary md:block dark:bg-bg-muted/50">
              {icon}
            </div>
          )}
        </div>

        {(description || trend) && (
          <div className="mt-1.5 flex items-center gap-2 md:mt-4">
            {trend && (
              <span
                className={cn(
                  'flex items-center text-xs font-semibold',
                  trend.isPositive ? 'text-success' : 'text-error',
                )}
              >
                {trend.isPositive ? '↑' : '↓'} {trend.value}%
              </span>
            )}
            {description && (
              <p className="text-[11px] text-text-tertiary md:text-xs">{description}</p>
            )}
          </div>
        )}

        {/* Decorative background element */}
        <div className="absolute -right-4 -bottom-4 h-24 w-24 rounded-full bg-primary/5 blur-2xl transition-all group-hover:bg-primary/10" />
      </motion.div>
    )
  },
)

StatCard.displayName = 'StatCard'

export { StatCard, type StatCardProps }
